import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CropFreeIcon from "@mui/icons-material/CropFree";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import Card from "@mui/material/Card";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { usePathology } from "./PathologyRoot";
import PathologyDiseaseSelect from "./PathologyDiseaseSelect";

const SEVERITIES = ["mild", "moderate", "severe"];

const overlayStatusLabel = (status) => {
  switch (status) {
    case "preview":
      return "Preview";
    case "accepted":
      return "Saved";
    case "generating":
    case "pending":
      return "Generating";
    case "failed":
      return "Failed";
    default:
      return status || "Unknown";
  }
};

export default function PathologyAdminSidebar({
  compact = true,
  minWidth = 280,
  maxWidth = 340,
}) {
  const { admin } = usePathology();
  const {
    diseases,
    selectedDiseaseId,
    setSelectedDiseaseId,
    selectedDisease,
    overlays,
    loading,
    error,
    newName,
    setNewName,
    newDescription,
    setNewDescription,
    newSeverity,
    setNewSeverity,
    roiDrawActive,
    setRoiDrawActive,
    roiBounds,
    activeOverlay,
    previewOpacity,
    setPreviewOpacity,
    customPrompt,
    setCustomPrompt,
    previewOverlay,
    isGenerating,
    acceptedOverlays,
    handleCreateDisease,
    handlePublishDisease,
    handleDeleteDisease,
    handleGenerate,
    handleRegenerate,
    handleAccept,
    handleDeleteOverlay,
    handleDiscardPreview,
  } = admin;

  const visibleOverlays = overlays.filter(
    (o) => o.status === "preview" || o.status === "accepted" || o.status === "failed"
  );

  return (
    <Card
      style={{
        marginBottom: 16,
        minWidth,
        maxWidth,
        width: "100%",
        overflow: "visible",
      }}
    >
      <MDBox p={2} sx={{ overflow: "visible" }}>
        <MDBox display="flex" alignItems="center" gap={1}>
          <AutoAwesomeIcon color="primary" fontSize="small" />
          <MDTypography
            variant={compact ? "button" : "h6"}
            fontWeight={compact ? "bold" : "regular"}
            sx={{ flex: 1 }}
          >
            AI Pathology
          </MDTypography>
          <Chip label="Admin" size="small" color="warning" variant="outlined" />
        </MDBox>
        <Divider sx={{ my: 1.5 }} />

        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Step 1 — Disease
          </Typography>
          <PathologyDiseaseSelect
            diseases={diseases}
            selectedDiseaseId={selectedDiseaseId}
            onSelectDiseaseId={setSelectedDiseaseId}
            newName={newName}
            setNewName={setNewName}
            newDescription={newDescription}
            setNewDescription={setNewDescription}
            newSeverity={newSeverity}
            setNewSeverity={setNewSeverity}
            onCreateDisease={handleCreateDisease}
            loading={loading}
            severities={SEVERITIES}
          />

          {selectedDisease ? (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                onClick={handlePublishDisease}
                disabled={loading || selectedDisease.status === "published"}
              >
                Publish
              </MDButton>
              <MDButton
                variant="outlined"
                color="error"
                size="small"
                onClick={handleDeleteDisease}
                disabled={loading}
              >
                <DeleteOutlineIcon sx={{ mr: 0.5, fontSize: 18 }} />
                Delete disease
              </MDButton>
            </Stack>
          ) : null}

          <Divider />

          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Step 2–4 — Select ROI on slide
          </Typography>
          <MDButton
            variant={roiDrawActive ? "gradient" : "outlined"}
            color={roiDrawActive ? "warning" : "info"}
            size="small"
            onClick={() => setRoiDrawActive((v) => !v)}
            disabled={!selectedDiseaseId}
            sx={{ width: "100%" }}
          >
            <CropFreeIcon sx={{ mr: 0.5, fontSize: 18 }} />
            {roiDrawActive ? "Drawing ROI…" : "Draw rectangle ROI"}
          </MDButton>
          {roiBounds ? (
            <Typography variant="caption" color="text.secondary">
              ROI selected — zoom to 40× if needed, then generate.
            </Typography>
          ) : null}

          <Divider />

          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Step 5 — Generate overlay
          </Typography>
          <TextField
            size="small"
            label="Custom prompt (optional)"
            multiline
            minRows={2}
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
          />
          <MDButton
            variant="gradient"
            color="dark"
            size="small"
            onClick={handleGenerate}
            disabled={loading || !roiBounds || !selectedDiseaseId}
            sx={{ width: "100%" }}
          >
            <AutoAwesomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
            Generate disease overlay
          </MDButton>

          {isGenerating ? (
            <Box>
              <Typography variant="caption" gutterBottom>
                Generating overlay… {activeOverlay?.progress ?? 0}%
              </Typography>
              <LinearProgress
                variant={
                  activeOverlay?.progress > 0 ? "determinate" : "indeterminate"
                }
                value={activeOverlay?.progress ?? 0}
                color="info"
              />
              <Stack direction="row" alignItems="center" spacing={1} mt={1}>
                <CircularProgress size={16} color="info" />
                <Typography variant="caption" color="text.secondary">
                  Estimated ~15–30 sec
                </Typography>
              </Stack>
            </Box>
          ) : null}

          {activeOverlay?.status === "failed" ? (
            <Alert
              severity="error"
              action={
                <MDButton
                  color="error"
                  size="small"
                  onClick={() => handleDeleteOverlay(activeOverlay.id)}
                >
                  Remove
                </MDButton>
              }
            >
              {activeOverlay.error_message || "Generation failed"}
            </Alert>
          ) : null}

          {previewOverlay ? (
            <>
              <Divider />
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Step 6 — Preview
                </Typography>
                <Tooltip title="Discard preview — removes overlay from slide">
                  <MDButton
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleDiscardPreview}
                    disabled={loading}
                  >
                    <CloseIcon sx={{ mr: 0.5, fontSize: 16 }} />
                    Discard
                  </MDButton>
                </Tooltip>
              </Stack>
              <Typography variant="caption">Overlay opacity on slide</Typography>
              <Slider
                size="small"
                min={0}
                max={1}
                step={0.05}
                value={previewOpacity}
                onChange={(_, v) => setPreviewOpacity(v)}
                color="info"
              />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <MDButton
                  variant="gradient"
                  color="success"
                  size="small"
                  onClick={handleAccept}
                  disabled={loading}
                >
                  <CheckIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  Accept
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  size="small"
                  onClick={handleRegenerate}
                  disabled={loading}
                >
                  <RefreshIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  Regenerate
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleDiscardPreview}
                  disabled={loading}
                >
                  <DeleteOutlineIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  Delete overlay
                </MDButton>
              </Stack>

              <Divider />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Step 7 — Explanation on map pin
              </Typography>
              <Box
                sx={{
                  p: 1.25,
                  borderRadius: 1,
                  bgcolor: "grey.50",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="caption" fontWeight={700} display="block">
                  {selectedDisease?.name || "Disease"}
                </Typography>
                {selectedDisease?.severity ? (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Severity: {selectedDisease.severity}
                  </Typography>
                ) : null}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 0.75, lineHeight: 1.45 }}
                >
                  {selectedDisease?.description?.trim() ||
                    "Add a disease description when creating the disease — it will appear on the map pin."}
                </Typography>
              </Box>
            </>
          ) : null}

          {visibleOverlays.length > 0 ? (
            <>
              <Divider />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Overlays on this disease
              </Typography>
              <Stack spacing={1}>
                {visibleOverlays.map((ov) => (
                  <Box
                    key={ov.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                      borderRadius: 1,
                      bgcolor: "grey.100",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" fontWeight={600} display="block">
                        {overlayStatusLabel(ov.status)}
                        {ov.id === activeOverlay?.id ? " (current)" : ""}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {ov.metadata_json?.crop_width || ov.roi_width}×
                        {ov.metadata_json?.crop_height || ov.roi_height} px
                      </Typography>
                    </Box>
                    <Tooltip title="Delete overlay from slide">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteOverlay(ov.id)}
                        disabled={loading || ov.status === "generating"}
                        aria-label="Delete overlay"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Stack>
            </>
          ) : acceptedOverlays.length > 0 ? (
            <Typography variant="caption" color="text.secondary">
              {acceptedOverlays.length} saved overlay
              {acceptedOverlays.length === 1 ? "" : "s"}
            </Typography>
          ) : null}
        </Stack>
      </MDBox>
    </Card>
  );
}
